// 微信公众号网页授权相关工具函数
// 在微信内置浏览器中发起 snsapi_userinfo 授权，授权后回到原页面并附带 code / state。
// 回调处理在 AuthCallbackHandler（providers/auth-provider.tsx）中统一处理。

const STATE_STORAGE_PREFIX = 'wx_state_';

/** 网页授权用途：登录、完整微信绑定或支付前静默绑定 */
export type WxOauthAction = 'login' | 'bind_wechat' | 'bind_wechat_pay';

interface WxStateInfo {
  state: string;
  action: WxOauthAction;
}

export function generateWxState(): string {
  const randomStr =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
  return randomStr.substring(0, 32);
}

export function storeWxStateInfo(
  state: string,
  action: WxOauthAction = 'login',
): void {
  const info: WxStateInfo = { state, action };
  sessionStorage.setItem(`${STATE_STORAGE_PREFIX}${state}`, JSON.stringify(info));
}

export function retrieveWxStateInfo(state: string): WxStateInfo | null {
  const key = `${STATE_STORAGE_PREFIX}${state}`;
  const raw = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'state' in parsed) {
      return parsed as WxStateInfo;
    }
    // 兼容历史格式：老版本只存了纯字符串 state
    return { state: raw, action: 'login' };
  } catch {
    return { state: raw, action: 'login' };
  }
}

export function buildWxOauthUrl(
  state: string,
  action: WxOauthAction = 'login',
): string {
  const redirectUri = encodeURIComponent(window.location.href);
  const scope =
    action === 'bind_wechat_pay' ? 'snsapi_base' : 'snsapi_userinfo';
  return `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${process.env.NEXT_PUBLIC_WX_APPID}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}&forcePopup=true&connect_redirect=1#wechat_redirect`;
}

/**
 * 触发微信公众号 snsapi_userinfo 网页授权跳转。
 * 调用后当前页面会被替换为微信 OAuth 页面，授权完成后回到原 URL。
 *
 * @param action 授权用途：'login'（默认，登录）或 'bind_wechat'（绑定微信到当前账号）
 */
export function redirectToWxOauth(action: WxOauthAction = 'login'): void {
  const state = generateWxState();
  storeWxStateInfo(state, action);
  window.location.href = buildWxOauthUrl(state, action);
}
